/**
 * Algo Performance Dashboard read API (2026-09-19). Mirrors
 * `backend/app/api/v1/algo_performance.py` route-for-route — both calls
 * are pure reads of an already-persisted snapshot; neither ever triggers
 * a backtest, scans trades, or computes anything on the backend. A
 * brand-new algo with nothing computed yet still returns 200 with
 * `data_quality_status: "NOT_YET_COMPUTED"`, never a 404 — callers don't
 * need to special-case the "nothing yet" state as an error.
 */
import api from "@/services/api";
import type { AlgoPerformanceSchedulerStatusRead, AlgoPerformanceSnapshotRead } from "@/types";

export const algoPerformanceService = {
  async getBacktestPerformance(algoId: string): Promise<AlgoPerformanceSnapshotRead> {
    const { data } = await api.get<AlgoPerformanceSnapshotRead>(`/algo/${algoId}/performance/backtest`);
    return data;
  },

  async getLivePerformance(algoId: string): Promise<AlgoPerformanceSnapshotRead> {
    const { data } = await api.get<AlgoPerformanceSnapshotRead>(`/algo/${algoId}/performance/live`);
    return data;
  },

  // Pre-production audit finding #6: admin-visible EOD scheduler status.
  async getSchedulerStatus(algoId: string): Promise<AlgoPerformanceSchedulerStatusRead> {
    const { data } = await api.get<AlgoPerformanceSchedulerStatusRead>(`/algo/${algoId}/performance/scheduler-status`);
    return data;
  },

  // 2026-09-21: the one WRITE call in this service. The EOD scheduler only
  // recomputes the LIVE snapshot once a day, so a same-day capital/config
  // change is otherwise invisible until the next day's tick — this forces
  // an immediate recompute and returns the freshly-written snapshot.
  async recomputeLivePerformance(algoId: string): Promise<AlgoPerformanceSnapshotRead> {
    const { data } = await api.post<AlgoPerformanceSnapshotRead>(`/algo/${algoId}/performance/recompute-live`);
    return data;
  },
};

export default algoPerformanceService;
